// components/common/FilterChips.jsx
//
// The row of mutually exclusive filter chips in a grid tile's toolbar
// ("Due today" / "All", "Active" / "Drafts" / "Completed"). Each chip can
// carry how many rows the grid shows once it is pressed. The page passes that
// number as the option's `count`, or leaves it out while it is not known yet:
// a chip then shows its bare label, never a guessed number.
//
// An option the page cannot answer stays in the row, grayed out, so the merchant
// sees what opens later; its `tooltip` says why. A disabled chip takes no pointer
// events, so the hover lands on a wrapper around it.
//
// Separate pills: on a phone the row wraps onto a second line and each pill
// still reads as one of the group. Where the row goes in a tile's toolbar,
// Tile.css gives it the whole line on a phone.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import { useFormatters } from '../../hooks/useFormatters';
import './FilterChips.css';

/**
 * @param {{value: string|number, label: string, count?: number, disabled?: boolean, tooltip?: string}[]} options
 * @param {string|number} value   The pressed option; one no option matches presses none.
 * @param {(value: string|number) => void} onChange
 * @param {boolean} [disabled]    The whole row, while the page is busy.
 */
export default function FilterChips({ options, value, onChange, disabled = false }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  return (
    <div className="filter-chips">
      {options.map((option) => {
        const pressed = option.value === value;
        const chip = (
          <Chip
            key={option.value}
            // A control, not a badge: the theme's small default is badge-sized.
            size="medium"
            variant={pressed ? 'filled' : 'outlined'}
            color={pressed ? 'primary' : 'default'}
            onClick={() => onChange(option.value)}
            disabled={disabled || option.disabled}
            className={option.disabled ? 'filter-chip-unavailable' : undefined}
            label={option.count === undefined
              ? option.label
              : t('common.labelWithCount', { label: option.label, count: formatNumber(option.count, 0) })}
          />
        );
        if (!option.tooltip) return chip;
        return (
          <Tooltip key={option.value} title={option.tooltip}>
            <span className={option.disabled ? 'filter-chip-unavailable-hover' : undefined}>{chip}</span>
          </Tooltip>
        );
      })}
    </div>
  );
}
